import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Knowledge Base Toggle Component
 * Provides a checkbox to enable/disable knowledge base functionality for parrots
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.5, 11.6
 */
const KnowledgeBaseToggle = ({
  enabled = false,
  onToggle,
  onClear,
  disabled = false,
  className = '',
  showDescription = true,
  size = 'default' // 'small', 'default', 'large'
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  /**
   * Handle toggle change with clearing logic
   * Requirements: 11.2 - Clear URL and file selections when knowledge base is disabled
   */
  const handleToggle = (checked) => {
    console.log('🎛️ KnowledgeBaseToggle handleToggle:', {
      checked,
      enabled,
      disabled,
      willClear: !checked && !!onClear
    });
    
    onToggle(checked);
    
    if (!checked && onClear) {
      // Clear URL and files when disabling KB
      console.log('🧹 KnowledgeBaseToggle calling onClear');
      onClear();
    }
  };

  // Size-based styling
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return {
          checkbox: 'h-3 w-3',
          label: 'text-xs',
          description: 'text-xs'
        };
      case 'large':
        return {
          checkbox: 'h-5 w-5',
          label: 'text-base',
          description: 'text-sm'
        };
      default:
        return {
          checkbox: 'h-4 w-4',
          label: 'text-sm',
          description: 'text-xs'
        };
    }
  };

  const sizeClasses = getSizeClasses();

  return (
    <div className={`knowledge-base-toggle ${className}`}>
      <div className="flex items-start space-x-3">
        <div className="flex items-center h-5">
          <input
            id="knowledge-base-toggle"
            type="checkbox"
            checked={enabled}
            onChange={(e) => handleToggle(e.target.checked)}
            disabled={disabled}
            className={`${sizeClasses.checkbox} rounded border-gray-300 text-teal-600 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed ${
              isDark ? 'border-gray-600 bg-gray-700' : 'bg-white'
            }`}
            aria-describedby={showDescription ? "knowledge-base-description" : undefined}
          />
        </div>
        <div className="flex-1">
          <label
            htmlFor="knowledge-base-toggle"
            className={`${sizeClasses.label} font-medium cursor-pointer ${
              disabled ? 'cursor-not-allowed opacity-50' : ''
            } ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            Enable Knowledge Base
          </label>
          {showDescription && (
            <div>
              {enabled ? (
                <p
                  id="knowledge-base-description"
                  className={`${sizeClasses.description} mt-1 ${
                    isDark ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  Add files and URLs to give your parrot access to specific knowledge
                </p>
              ) : (
                <p
                  id="knowledge-base-description"
                  className={`${sizeClasses.description} mt-1 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  Enable this to give your AI agent access to your uploaded files and website content
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Status indicator when enabled */}
      {enabled && (
        <div className={`mt-2 flex items-center space-x-2 ${
          isDark ? 'text-teal-400' : 'text-teal-600'
        }`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-xs font-medium">Knowledge Base Enabled</span>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseToggle;